import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "react-aria-components";
import type { PmActivity, PmMicroScenarioView } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../auth/keycloak.js";
import { formatDateTime } from "./pm-labels.js";
import { useInstituteLabel, usePmInstitutes } from "./pm-institutes.js";
import { activityTaskId, activityText } from "./pm-activity.js";
import { StatusChip } from "./StatusChip.js";
import { usePmMsActivity } from "./usePmBoard.js";

const ALL = "all";
const NONE = "none";
type Period = "all" | "since" | "today" | "7d";

/** Whether a row counts as "new" for this person: after their previous visit, and not their own doing. */
export function isNewActivity(a: PmActivity, lastVisitAt: string | null, meId: number): boolean {
  return lastVisitAt !== null && a.createdAt > lastVisitAt && a.actor.userId !== meId;
}

/**
 * The Verlauf (ADR-055 D5, phase 6b): everything that happened in the MS's current phase, newest first, filterable by
 * institute, person and period. Rows newer than the caller's previous visit — by someone else — carry "neu" ("seit dem
 * letzten Besuch"). The institute shown is the one the actor had WHEN they acted (the log's snapshot).
 */
export function PmVerlauf({
  session,
  meId,
  data,
  lastVisitAt,
  onOpenTask,
}: {
  session: AuthSession;
  meId: number;
  data: PmMicroScenarioView;
  /** The caller's previous visit; `undefined` while it is being recorded. */
  lastVisitAt: string | null | undefined;
  onOpenTask: (taskId: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const institutes = usePmInstitutes();
  const instituteLabel = useInstituteLabel();
  const q = usePmMsActivity(session, data.ms.id);
  const [institute, setInstitute] = useState<string>(ALL);
  const [person, setPerson] = useState<string>(ALL);
  const [period, setPeriod] = useState<Period>("all");

  const rows = q.data ?? [];
  const people = [...new Map(rows.map((a) => [a.actor.userId, a.actor.name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const visit = lastVisitAt ?? null;
  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const shown = rows.filter((a) => {
    if (institute !== ALL && (institute === NONE ? a.actor.institute !== null : a.actor.institute !== institute)) return false;
    if (person !== ALL && a.actor.userId !== Number(person)) return false;
    const at = Date.parse(a.createdAt);
    if (period === "since") return isNewActivity(a, visit, meId);
    if (period === "today") return at >= startOfToday.getTime();
    if (period === "7d") return at >= now - 7 * 24 * 3600 * 1000;
    return true;
  });
  const stepNumber = (stepId: number) => data.steps.find((s) => s.id === stepId)?.number ?? null;
  const taskTitle = (a: PmActivity): string | null => {
    const id = activityTaskId(a);
    if (id === null) return null;
    // The title the row recorded (the task as it was called then); a task row's own create / rename / delete snapshot
    // has it as `title`. Only rows written before `taskTitle` existed fall back to the task's current title.
    const snap = (a.after ?? a.before ?? {}) as Record<string, unknown>;
    const recorded = snap.taskTitle ?? (a.objectType === "task" ? snap.title : undefined);
    if (typeof recorded === "string") return recorded;
    return data.tasks.find((x) => x.id === id)?.title ?? null;
  };

  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">{t("pmBoard.nav.verlauf")}</h1>
        <p className="text-muted-foreground m-0 text-sm">{t("pmBoard.verlauf.intro")}</p>
        {lastVisitAt !== undefined ? (
          <p className="text-muted-foreground m-0 text-xs">
            {visit ? t("pmBoard.verlauf.lastVisit", { date: formatDateTime(visit, i18n.language) }) : t("pmBoard.verlauf.firstVisit")}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Select selectedKey={institute} onSelectionChange={(k) => k !== null && setInstitute(String(k))} className="flex w-48 flex-col gap-1.5">
          <Label className="text-muted-foreground text-xs">{t("pmBoard.verlauf.institute")}</Label>
          <SelectTrigger />
          <SelectContent>
            <SelectItem id={ALL}>{t("pmBoard.verlauf.allInstitutes")}</SelectItem>
            {institutes.map((i) => (
              <SelectItem key={i.key} id={i.key}>
                {i.label}
              </SelectItem>
            ))}
            <SelectItem id={NONE}>{t("pmBoard.institute.none")}</SelectItem>
          </SelectContent>
        </Select>
        <Select selectedKey={person} onSelectionChange={(k) => k !== null && setPerson(String(k))} className="flex w-48 flex-col gap-1.5">
          <Label className="text-muted-foreground text-xs">{t("pmBoard.verlauf.person")}</Label>
          <SelectTrigger />
          <SelectContent>
            <SelectItem id={ALL}>{t("pmBoard.verlauf.everyone")}</SelectItem>
            {people.map(([id, name]) => (
              <SelectItem key={id} id={String(id)}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select selectedKey={period} onSelectionChange={(k) => k !== null && setPeriod(k as Period)} className="flex w-56 flex-col gap-1.5">
          <Label className="text-muted-foreground text-xs">{t("pmBoard.verlauf.period")}</Label>
          <SelectTrigger />
          <SelectContent>
            <SelectItem id="all">{t("pmBoard.verlauf.periodAll")}</SelectItem>
            <SelectItem id="since">{t("pmBoard.verlauf.periodSince")}</SelectItem>
            <SelectItem id="today">{t("pmBoard.verlauf.periodToday")}</SelectItem>
            <SelectItem id="7d">{t("pmBoard.verlauf.period7")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {q.isPending ? (
        <p role="status" className="text-muted-foreground m-0 text-sm">
          {t("pmBoard.loading")}
        </p>
      ) : q.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-destructive m-0 text-sm">
            {t("pmBoard.verlauf.loadFailed")}
          </p>
          <Button variant="outline" size="sm" onPress={() => void q.refetch()}>
            {t("pmBoard.tryAgain")}
          </Button>
        </div>
      ) : shown.length === 0 ? (
        <p className="bg-background text-muted-foreground m-0 rounded-xl p-5 text-sm shadow-sm">
          {rows.length === 0 ? t("pmBoard.verlauf.emptyAll") : t("pmBoard.verlauf.empty")}
        </p>
      ) : (
        <ol className="bg-background m-0 flex list-none flex-col rounded-xl p-0 shadow-sm">
          {shown.map((a) => {
            const fresh = isNewActivity(a, visit, meId);
            const title = taskTitle(a);
            const taskId = activityTaskId(a);
            const openable = taskId !== null && data.tasks.some((x) => x.id === taskId);
            // "hat den Task angelegt · „X“" for the task itself, "hat eine Anmerkung geschrieben an „X“" for a note or file on it.
            const ref = title === null ? null : t(a.objectType === "task" ? "pmBoard.activity.taskRef" : "pmBoard.activity.onTask", { title });
            return (
              <li key={a.id} className={cn("border-border flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b px-4 py-2.5 last:border-b-0", fresh && "bg-muted/60")}>
                {fresh ? <StatusChip tone="attention">{t("pmBoard.verlauf.new")}</StatusChip> : null}
                <span className="min-w-0 flex-1 text-sm">
                  <strong className="font-semibold">{t("pmBoard.activity.actor", { institute: instituteLabel(a.actor), name: a.actor.name })}</strong>{" "}
                  {activityText(t, a, { stepNumber })}
                  {ref !== null ? (
                    <>
                      {" "}
                      {openable ? (
                        <Button variant="link" size="xs" onPress={() => onOpenTask(taskId)} className="h-auto p-0 text-sm">
                          {ref}
                        </Button>
                      ) : (
                        ref
                      )}
                    </>
                  ) : null}
                </span>
                <span className="text-muted-foreground text-xs tabular-nums">{formatDateTime(a.createdAt, i18n.language)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
